<template>
    <div v-if="panel_visible">
        <datalist id="panel_titles">
            <option v-for="title in panel_titles[publication.subject]" :value="title"/>
        </datalist>
        <b-list-group-item>
            <h3 v-if="!article && !unit && !unit_group">Panels</h3>
            <b-btn size="sm" variant="success" @click="newPanel" :disabled="!can_create_panel || disabled">
                <font-awesome-icon :icon="['fad', 'plus-square']" /> New Panel
            </b-btn>
        </b-list-group-item>
        <b-list-group-item v-for="panel in panels" :key="panel.id">
            <div v-if="!panel.deleted_at" class="d-flex align-items-center">
                <div
                    class="px-3 flex-column d-flex align-self-stretch"
                    style="justify-content: space-between; border-right: 1px solid #999; margin-left: -1.25em"
                    :style="colors.getColor(panel.color)"
                >
                    <b-btn
                        size="sm"
                        variant="outline-primary"
                        class='mt-3'
                        :disabled="panels.indexOf(panel) <= 0 || disabled"
                        @click='panelSortUp(panel)'
                    >
                        <font-awesome-icon :icon="['fas', 'caret-up']" fixed-width />
                    </b-btn>
                    <b-btn
                        size="sm"
                        variant="outline-primary"
                        class='mb-3'
                        :disabled="panels.indexOf(panel) >= (panels.length - 1) || disabled"
                        @click='panelSortDown(panel)'
                    >
                        <font-awesome-icon :icon="['fas', 'caret-down']" fixed-width />
                    </b-btn>
                </div>
                <div class="flex-grow-1 pl-3">
                    <b-form-group

                    >
                        <label>Panel Title</label>
                        <b-form-input
                            type="text"
                            placeholder="Enter Title or Select From List"
                            aria-label="title"
                            list='panel_titles'
                            v-model="panel.title"
                            @change="savePanel(panel)"
                            :disabled="disabled"
                            :lazy="true"
                        />
                    </b-form-group>
                    <b-form-group>
                        <label>Panel Header Color</label>
                        <b-form-select
                            v-model="panel.color"
                            :options="colorOptions"
                            @change="savePanel(panel)"
                            :disabled="disabled"
                        ></b-form-select>
                    </b-form-group>
                    <div>
                        <strong>Text</strong>
                        <div v-if="panel.blocks.length">
                                <TipTap
                                    v-model="panel.blocks[0].text"
                                    :publication="publication"
                                    :panel="panel"
                                    :menu_items="[
                                        'bold',
                                        'italic',
                                        'underline',
                                        'ordered_list',
                                        'bullet_list',
                                        'interactive_media_link',
                                        'image',
                                        'hard_break',
                                        'highlight',
                                        'highlight_orange',
                                        'highlight_blue',
                                        'highlight_green',
                                        'highlight_black',
                                        'highlight_white',
                                        'page_break',
                                        'edit_html',
                                        'paragraph_alignment',
                                    ]"
                                    @input=""
                                    @blur="savePanel(panel)"
                                    @save="savePanel(panel)"
                                    @update_tiptap_table="update_panel_from_tiptap($event, panel)"
                                    :readonly="disabled"
                                />
                                <b-btn
                                    size="sm"
                                    class='ml-3'
                                    variant="outline-danger"
                                    @click="removeText(panel)"
                                    :disabled="!can_update_panel || disabled"
                                >
                                <span>
                                    <font-awesome-icon :icon="['fad', 'minus-square']" /> Remove Text
                                </span>
                            </b-btn>
                        </div>
                        <div v-else>
                            <div class="mb-2"><small><em>No Text</em></small></div>
                            <b-btn
                                size="sm"
                                class="ml-3"
                                variant="outline-success"
                                @click="panel.blocks.push({title: '', text: ''})"
                                :disabled="!can_update_panel || disabled"
                            >
                                <span>
                                    <font-awesome-icon :icon="['fad', 'plus-square']" /> Add Text
                                </span>
                            </b-btn>

                        </div>
                    </div>

                    <div class="mt-2">
                        <panel-media
                            :publication="publication"
                            :article="article"
                            :unit="unit"
                            :panel="panel"
                            @updateMediaOrder="handleUpdateMediaOrder(panel, $event)"
                            @savePanel="handleSave"
                            :disabled="disabled"
                        />
                    </div>
                    <b-btn
                        size="sm"
                        class='mt-3'
                        variant="danger"
                        @click="removePanel(panel)"
                        :disabled="!can_delete_panel || disabled"
                    >
                        <font-awesome-icon :icon="['fad', 'times-square']" /> Remove Panel
                    </b-btn>
                </div>
            </div>
        </b-list-group-item>
    </div>
</template>

<style lang="scss" scoped>

</style>

<script>
import { mapState } from 'vuex'
import TipTap from '@/components/tiptap/TipTap'
import eventBus from '@/common/event-bus'
import PanelMedia from '@/components/panels/panel_media/Index'
import colors from '@/common/colors'

export default {
    data: () => ({
        colorOptions: [
            { value: 'none', text: 'None' },
            { value: 'green', text: 'Green'},
            { value: 'blue', text: 'Blue'},
            { value: 'orange', text: 'Orange'},
            { value: 'red', text: 'Red'},
        ],
        current_panel: null,
        panel_titles: {
            "science": [
                'Week Storyline',
                'Science and Engineering Practices',
                'Disciplinary Core Ideas',
                'Cognitive Demands for Science',
                'Weekly Phenomenon',
                'Teacher Background Knowledge',
            ],
            "social_studies": [
                'Curriculum Notes',
                'Historical Thinking Skills',
                'Teacher Background Knowledge',
            ]
        },
        panel_visible: true,
        colors: colors,
    }),
    props: {
        panels: Array,
        publication: Object,
        unit_group: {
            type: Object,
            default: null,
        },
        unit: {
            type: Object,
            default: null,
        },
        article: {
            type: Object,
            default: null,
        },
        disabled: {
            type: Boolean,
            default: false,
        }
    },
    computed: {
        ...mapState({
            wysiwyg_menu_type: state => state.settings.wysiwyg_menu_type,
            user: state => state.user.user
        }),
        can_create_panel(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'create_panel');
        },
        can_delete_panel(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'delete_panel');
        },
        can_update_panel(){
            return this.user && this.user.permissions && _.includes(this.user.permissions, 'update_panel');
        }
    },
    components: {
        TipTap,
        PanelMedia
    },
    methods: {
        update_panel_from_tiptap(event, panel){
            panel.blocks[0].text = event;
            this.savePanel(panel);
        },
        handleSave(panel) {
            this.savePanel(panel)
            this.refreshPanel()
        },
        refreshPanel() {
            this.panel_visible = false
            this.$nextTick(() => {
                this.panel_visible = true;
            });
        },
        handleUpdateMediaOrder(panelToUpdate, updatedMedia) {
            panelToUpdate.media = updatedMedia;
            this.savePanel(panelToUpdate)
            this.refreshPanel()
        },
        panelSortUp(panel){
            let index = this.panels.indexOf(panel)
            if(index <= 0){
                return
            }
            let temp = this.panels[index-1]
            this.$set(this.panels, index-1, panel)
            this.$set(this.panels, index, temp)
            this.savePanel(panel)
            this.refreshPanel()
        },
        panelSortDown(panel){
            let index = this.panels.indexOf(panel)
            if(index >= this.panels.length-1){
                return
            }
            let temp = this.panels[index+1]
            this.$set(this.panels, index+1, panel)
            this.$set(this.panels, index, temp)
            this.savePanel(panel)
            this.refreshPanel()
        },
        newPanel(){
            let newPanel = {
              type: "text_block",
              color: "none",
              media: [
              ],
              blocks: [
              ],
              title: null
            }
            if(this.unit){
                newPanel.unit_id = this.unit.id
            }
            if(this.unit_group){
                newPanel.unit_group_id = this.unit_group.id
            }
            if(this.article){
                newPanel.article_id = this.article.id
            }
            if(!this.unit && !this.unit_group && !this.article){
                newPanel.publication_id = this.publication.id
            }
            this.panels.push(newPanel)

            this.savePanel(newPanel)
        },
        savePanel(panel){
            panel.order_number = this.panels.indexOf(panel)+1
            this.axios.post('/api/panels/save', panel)
            .then(response => {
                panel.id = response.data.id
            })
            .catch(error => {
            })
        },
        removePanel(panel){
            this.$swal({
                html: 'Are you sure you want to remove this panel?',
                showCancelButton: true,
                confirmButtonText: 'Remove Panel',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    this.axios.post('/api/panels/remove', panel)
                    .then(response => {
                        this.panels.splice(this.panels.indexOf(panel), 1);
                    })
                    .catch(error => {
                    })
                }
            })
        },
        removeText(panel) {
            this.$swal({
                html: 'You will lose all current panel text if you continue',
                showCancelButton: true,
                confirmButtonText: 'Remove Text',
                cancelButtonText: 'Cancel',
                reverseButtons: true
            }).then((result) => {
                if (result.value) {
                    panel.blocks.splice(0, 1);
                    this.savePanel(panel)
                    this.refreshPanel()
                }
            })
        }
    },
    created() {
        // emit an event letting publication know when unit panel is created, this is so scroll doesn't happen before panels are loaded -KO
        if (this.unit) {
            eventBus.$emit('panels-created')
        }
    }
}
</script>
